// @flow

import type {
  BabyListInvoiceState,
  ItemToInvoice,
  BabyListInvoiceItem,
  SearchItem,
  BabyListInvoice,
  Supplier,
  Action,
} from "../types"
import {isNil, find, flatMap, uniqBy, map, groupBy} from "lodash"

const initialState: BabyListInvoiceState = {
  babyListInvoice: null,
  itemsToInvoice: [],
  searchItems: [],
  isProcessing: false,
  finaliseError: false,
  suppliers: [],
}

const babyListInvoice = (
  state: BabyListInvoiceState = initialState,
  action: Action
) => {
  switch (action.type) {
    case "SET_BABY_LIST_INVOICE_FINALISE_ERROR":
      return {
        ...state,
        finaliseError: action.finaliseError,
      }
    case "SET_BABY_LIST_INVOICE":
      return {
        ...state,
        babyListInvoice: action.babyListInvoice,
      }
    case "SET_BABY_LIST_INVOICE_SEARCH_ITEMS":
      return {
        ...state,
        searchItems: action.searchItems,
      }
    case "SET_BABY_LIST_INVOICE_ITEMS_TO_INVOICE":
      return {
        ...state,
        itemsToInvoice: action.itemsToInvoice,
      }
    case "SET_BABY_LIST_INVOICE_PROCESSING":
      return {
        ...state,
        isProcessing: action.isProcessing,
      }
    case "SET_BABY_LIST_INVOICE_SUPPLIERS":
      return {
        ...state,
        suppliers: action.suppliers,
      }
    default:
      return state
  }
}

export const getItemToInvoice = (
  itemsToInvoice: $ReadOnlyArray<ItemToInvoice>,
  babyListItemId: number
): ?ItemToInvoice => {
  const flat = flatMap(itemsToInvoice, i => (i.items ? i.items : i))
  return find(flat, i => i.id == babyListItemId)
}

export const getBabyListInvoiceItem = (
  babyListInvoice: BabyListInvoice,
  babyListInvoiceItemId: number
): ?BabyListInvoiceItem => {
  const flat = flatMap(babyListInvoice.itemsGrouped, i =>
    i.items ? i.items : i
  )
  return find(flat, i => i.id == babyListInvoiceItemId)
}

// groups itemsToInvoice from a flat list of items and sets
// to a grouped lists where identical items are grouped and
// given a count and sets have their items nested
// We process this in two pieces: sets and single products,
// because we don't want sets to be grouped.
export const groupItemsToInvoice = (
  apiItemsToInvoice: $ReadOnlyArray<ItemToInvoice>
): $ReadOnlyArray<ItemToInvoice> => {
  // sets
  const sets = apiItemsToInvoice.filter(i => !isNil(i.items))
  const enrichedSets = sets.map(i => {
    // group the nested set items
    const groupedSetItems = groupBy(i.items, i => i.id)
    // groupedSetItems -> {'1', [{}, {}, ...]}
    const reduced = map(groupedSetItems, items => {
      const first = items[0]
      return {
        ...first,
        amount: items.length,
      }
    })
    return {
      ...i,
      items: reduced,
      amount: 1,
    }
  })

  // singles
  const singles = apiItemsToInvoice.filter(i => isNil(i.items))
  const groupedSingles = groupBy(singles, i => i.id)
  const enrichedSingles = singles.map(i => {
    return {
      ...i,
      amount: groupedSingles[i.id].length,
    }
  })
  const uniqGroupedSingles = uniqBy(enrichedSingles, i => i.id)

  // combine both
  return uniqGroupedSingles.concat(enrichedSets)
}

export const getFullItem = (
  searchItems: $ReadOnlyArray<SearchItem>,
  babyListItemId: number
): ?SearchItem => {
  const flat = flatMap(searchItems, i => (i.items ? i.items : i))
  return find(flat, i => i.id == babyListItemId)
}

export const getSupplier = (
  suppliers: $ReadOnlyArray<Supplier>,
  supplierId: number
) => find(suppliers, s => s.id == supplierId)

export default babyListInvoice
